/*
 * <announcement-bar> - rotating message carousel.
 *
 * Wraps an inner <ul> of <li> slides. When 2+ slides are present, this
 * component auto-rotates them on a configurable interval, exposes
 * prev/next chevron buttons, pauses on hover/focus-within, and respects
 * prefers-reduced-motion (no auto-rotate; chevrons still work).
 *
 * Reads:
 *   data-interval (ms): auto-rotate interval. Default 4200.
 *   data-pause-on-hover: "true" | "false". Default "true".
 *
 * Markup contract (rendered by sections/announcement-bar.liquid when 2+ blocks):
 *   <announcement-bar data-interval="4200" data-pause-on-hover="true">
 *     <button class="ab__prev" aria-label="...">...</button>
 *     <ul role="list" class="ab__track">
 *       <li role="listitem" data-active>...</li>
 *       <li role="listitem" hidden>...</li>
 *     </ul>
 *     <button class="ab__next" aria-label="...">...</button>
 *     <span class="visually-hidden" aria-live="polite" data-ab-announce></span>
 *   </announcement-bar>
 *
 * Without JS: the first slide remains visible (others stay hidden because
 * the Liquid section sets [hidden] on slides 2+). Chevrons are inert.
 *
 * Single-slide markup does NOT use <announcement-bar> at all - the section
 * renders a plain <div role="region">.
 */
class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this._interval = null;
    this._index = 0;
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._pause = this._pause.bind(this);
    this._resume = this._resume.bind(this);
    this._handleReducedMotion = this._handleReducedMotion.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.ab__track > li'));
    this.prev = this.querySelector('.ab__prev');
    this.next = this.querySelector('.ab__next');
    this.announceEl = this.querySelector('[data-ab-announce]');
    if (this.slides.length < 2) return;

    this.intervalMs = parseInt(this.dataset.interval || '4200', 10);
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

    if (this.prev) this.prev.addEventListener('click', this._onPrev);
    if (this.next) this.next.addEventListener('click', this._onNext);

    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', this._pause);
      this.addEventListener('pointerleave', this._resume);
      this.addEventListener('focusin', this._pause);
      this.addEventListener('focusout', this._resume);
    }

    this._reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reducedMotionMQ.addEventListener('change', this._handleReducedMotion);
    if (!this._reducedMotionMQ.matches) this._startTimer();
  }

  disconnectedCallback() {
    this._stopTimer();
    if (this.prev) this.prev.removeEventListener('click', this._onPrev);
    if (this.next) this.next.removeEventListener('click', this._onNext);
    if (this.pauseOnHover) {
      this.removeEventListener('pointerenter', this._pause);
      this.removeEventListener('pointerleave', this._resume);
      this.removeEventListener('focusin', this._pause);
      this.removeEventListener('focusout', this._resume);
    }
    if (this._reducedMotionMQ) {
      this._reducedMotionMQ.removeEventListener('change', this._handleReducedMotion);
    }
  }

  _startTimer() {
    this._stopTimer();
    this._interval = setInterval(() => this._advance(1, false), this.intervalMs);
  }

  _stopTimer() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  _pause(event) {
    if (event && event.relatedTarget && this.contains(event.relatedTarget)) return;
    this._stopTimer();
  }
  _resume(event) {
    if (event && event.relatedTarget && this.contains(event.relatedTarget)) return;
    if (this._reducedMotionMQ && this._reducedMotionMQ.matches) return;
    this._startTimer();
  }

  _handleReducedMotion(event) {
    if (event.matches) this._stopTimer();
    else this._startTimer();
  }

  _onPrev() { this._advance(-1, true); }
  _onNext() { this._advance(1, true); }

  _advance(delta, announce) {
    const total = this.slides.length;
    const nextIndex = (this._index + delta + total) % total;
    if (nextIndex === this._index) return;

    this.slides[this._index].hidden = true;
    this.slides[this._index].removeAttribute('data-active');
    this.slides[nextIndex].hidden = false;
    this.slides[nextIndex].setAttribute('data-active', '');
    this._index = nextIndex;

    if (announce && this.announceEl) {
      this.announceEl.textContent = this.slides[nextIndex].textContent.trim();
    }
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
